import React, { useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import {
  DollarSignIcon,
  PlusIcon,
  XIcon,
  CheckCircleIcon,
  Loader2Icon,
  CreditCardIcon,
  FileTextIcon,
  CalendarIcon,
} from 'lucide-react'
import { supabase } from '../lib/supabase'
import { ActivityTypes, logActivity, logActivityOnLinkedParents } from '../utils/activityLogger'
import { syncMilestonePaidForInvoice } from '../lib/syncMilestonePaid'
import { ColumnSelector } from '../components/ColumnSelector'
import { useColumnSelector } from '../hooks/useColumnSelector'
import { useAuth } from '../contexts/AuthContext'
import { useDivision } from '../contexts/DivisionContext'
import { matchesDivisionWithFallback } from '../lib/divisionFiltering'
import {
  MobilePage,
  MobilePageHeader,
  MobileListRow,
  MobileRowAction,
  MobileEmptyState,
  MobileFilterBar,
  MobileStatStrip,
  MobileSection,
  mobile,
} from '../components/mobile'

interface Payment {
  id: string
  invoice_id: string
  customer_id: string
  payment_date: string
  amount: number
  payment_method: string
  reference_number?: string
  notes?: string
  created_at: string
  voided_at?: string | null
  void_reason?: string | null
  invoice?: {
    invoice_number: string
    total: number
    balance_due: number
    operational_division_id?: string | null
    service_type?: string | null
    contract?: { operational_division_id?: string | null; service_type?: string | null } | null
  }
  customer?: { company_name?: string; contact_first_name?: string; contact_last_name?: string }
}

interface InvoiceOption {
  id: string
  invoice_number: string
  customer_id: string
  property_id?: string | null
  total: number
  balance_due: number
  amount_paid: number
  status: string
  operational_division_id?: string | null
  service_type?: string | null
  contract?: { operational_division_id?: string | null; service_type?: string | null } | null
  customer?: { company_name?: string }
}

const PAYMENT_METHODS = [
  { value: 'check', label: 'Check' },
  { value: 'cash', label: 'Cash' },
  { value: 'credit_card', label: 'Credit Card' },
  { value: 'ach', label: 'ACH / Bank Transfer' },
  { value: 'online', label: 'Online Payment' },
  { value: 'wire', label: 'Wire Transfer' },
  { value: 'other', label: 'Other' },
]

export function Payments() {
  const navigate = useNavigate()
  const { user } = useAuth()
  const { currentDivision } = useDivision()
  const [searchParams] = useSearchParams()
  const preselectedInvoiceId = searchParams.get('invoiceId')

  const [payments, setPayments] = useState<Payment[]>([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [showPaymentForm, setShowPaymentForm] = useState(!!preselectedInvoiceId)

  const [invoiceOptions, setInvoiceOptions] = useState<InvoiceOption[]>([])
  const [selectedInvoice, setSelectedInvoice] = useState<InvoiceOption | null>(null)
  const [saving, setSaving] = useState(false)

  const [formData, setFormData] = useState({
    invoice_id: preselectedInvoiceId || '',
    payment_date: new Date().toISOString().split('T')[0],
    amount: '',
    payment_method: 'check',
    reference_number: '',
    notes: '',
    sync_invoice_to_qb: true,
    sync_payment_to_qb: true,
  })

  useEffect(() => {
    fetchPayments()
    fetchUnpaidInvoices()
  }, [currentDivision])

  useEffect(() => {
    if (preselectedInvoiceId && invoiceOptions.length > 0) {
      const inv = invoiceOptions.find(i => i.id === preselectedInvoiceId)
      if (inv) {
        setSelectedInvoice(inv)
        setFormData(prev => ({
          ...prev,
          invoice_id: inv.id,
          amount: String(inv.balance_due || inv.total || 0),
        }))
      }
    }
  }, [preselectedInvoiceId, invoiceOptions])

  const fetchPayments = async () => {
    try {
      setLoading(true)
      const { data, error } = await supabase
        .from('payments')
        .select('*, invoice:invoices(invoice_number, total, balance_due, operational_division_id, contract:contracts(operational_division_id, service_type)), customer:customers(company_name, contact_first_name, contact_last_name)')
        .order('payment_date', { ascending: false })

      if (error) throw error
      setPayments((data || []).filter((payment: any) => {
        const invoice = Array.isArray(payment.invoice) ? payment.invoice[0] || null : payment.invoice
        const contract = Array.isArray(invoice?.contract) ? invoice.contract[0] || null : invoice?.contract
        payment.invoice = invoice ? { ...invoice, contract } : null
        return matchesDivisionWithFallback(invoice, currentDivision, contract)
      }))
    } catch (error) {
      console.error('Error fetching payments:', error)
      setPayments([])
    } finally {
      setLoading(false)
    }
  }

  const fetchUnpaidInvoices = async () => {
    try {
      const { data, error } = await supabase
        .from('invoices')
        .select('*, customer:customers(company_name), contract:contracts(operational_division_id, service_type)')
        .gt('balance_due', 0)
        .neq('status', 'draft')
        .order('due_date', { ascending: true })

      if (error) throw error
      setInvoiceOptions(
        (data || [])
          .map((inv: any) => ({
            ...inv,
            contract: Array.isArray(inv.contract) ? inv.contract[0] || null : inv.contract,
            total: Number(inv.total || inv.subtotal || 0),
            balance_due: Number(inv.balance_due || inv.total || inv.subtotal || 0),
            amount_paid: Number(inv.amount_paid || 0),
          }))
          .filter((inv: InvoiceOption) =>
            matchesDivisionWithFallback(inv, currentDivision, inv.contract)
          )
      )
    } catch (error) {
      console.error('Error fetching invoices:', error)
    }
  }

  const handleInvoiceSelect = (invoiceId: string) => {
    const inv = invoiceOptions.find(i => i.id === invoiceId)
    setSelectedInvoice(inv || null)
    setFormData(prev => ({
      ...prev,
      invoice_id: invoiceId,
      amount: inv ? String(inv.balance_due || inv.total || 0) : '',
    }))
  }

  const handleSubmitPayment = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!formData.invoice_id || !formData.amount || !formData.payment_date) {
      alert('Please fill in all required fields')
      return
    }

    const paymentAmount = parseFloat(formData.amount)
    if (isNaN(paymentAmount) || paymentAmount <= 0) {
      alert('Please enter a valid payment amount')
      return
    }

    try {
      setSaving(true)

      // record_invoice_payment inserts the payment and atomically recomputes
      // the invoice's amount_paid/balance_due/status (partial/paid) so the
      // status is always payment-driven rather than recomputed ad hoc here.
      const { data: rpcData, error: rpcError } = await supabase.rpc('record_invoice_payment', {
        p_invoice_id: formData.invoice_id,
        p_amount: paymentAmount,
        p_payment_date: formData.payment_date,
        p_payment_method: formData.payment_method,
        p_reference_number: formData.reference_number || null,
        p_notes: formData.notes || null,
        p_recorded_by: user?.id || null,
        p_sync_invoice_to_qb: formData.sync_invoice_to_qb,
        p_sync_payment_to_qb: formData.sync_payment_to_qb,
      })

      if (rpcError) throw rpcError
      const result = Array.isArray(rpcData) ? rpcData[0] : rpcData

      const invoice = selectedInvoice
      if (invoice?.customer_id) {
        const paymentDescription = `Payment of $${paymentAmount.toFixed(2)} recorded for invoice ${invoice.invoice_number}`
        await logActivity({
          activity_type: ActivityTypes.PAYMENT_RECEIVED,
          entity_type: 'customer',
          entity_id: invoice.customer_id,
          description: paymentDescription,
          metadata: {
            invoice_id: formData.invoice_id,
            amount: paymentAmount,
            property_id: invoice.property_id,
          },
        })
        await logActivityOnLinkedParents({
          parents: {
            customer_id: invoice.customer_id,
            property_id: invoice.property_id,
          },
          activity_type: ActivityTypes.PAYMENT_RECEIVED,
          description: paymentDescription,
          metadata: {
            invoice_id: formData.invoice_id,
            amount: paymentAmount,
          },
        })
      }

      if (result?.new_status === 'paid') {
        await syncMilestonePaidForInvoice(formData.invoice_id)
      }

      setFormData({
        invoice_id: '',
        payment_date: new Date().toISOString().split('T')[0],
        amount: '',
        payment_method: 'check',
        reference_number: '',
        notes: '',
        sync_invoice_to_qb: true,
        sync_payment_to_qb: true,
      })
      setSelectedInvoice(null)
      setShowPaymentForm(false)
      await fetchPayments()
      await fetchUnpaidInvoices()
    } catch (err: any) {
      console.error('Error recording payment:', err)
      alert('Failed to record payment: ' + (err.message || 'Unknown error'))
    } finally {
      setSaving(false)
    }
  }

  const handleVoidPayment = async (payment: Payment) => {
    const reason = window.prompt('Reason for voiding this payment (optional):')
    if (reason === null) return
    try {
      const { error } = await supabase.rpc('void_payment', {
        p_payment_id: payment.id,
        p_voided_by: user?.id || null,
        p_reason: reason || null,
      })
      if (error) throw error
      await logActivity({
        activity_type: ActivityTypes.PAYMENT_VOIDED,
        entity_type: 'customer',
        entity_id: payment.customer_id,
        description: `Payment of $${Number(payment.amount || 0).toFixed(2)} voided for invoice ${payment.invoice?.invoice_number || ''}`,
        metadata: { invoice_id: payment.invoice_id, amount: payment.amount },
      })
      await fetchPayments()
      await fetchUnpaidInvoices()
    } catch (err: any) {
      alert('Failed to void payment: ' + (err.message || 'Unknown error'))
    }
  }

  const getCustomerName = (payment: Payment) => {
    return payment.customer?.company_name ||
      `${payment.customer?.contact_first_name || ''} ${payment.customer?.contact_last_name || ''}`.trim() ||
      'Unknown'
  }

  const getMethodLabel = (method: string) => {
    return PAYMENT_METHODS.find(m => m.value === method)?.label || method
  }

  const filteredPayments = payments.filter(p => {
    const name = getCustomerName(p)
    const invNum = p.invoice?.invoice_number || ''
    const ref = p.reference_number || ''
    const term = searchTerm.toLowerCase()
    return name.toLowerCase().includes(term) ||
      invNum.toLowerCase().includes(term) ||
      ref.toLowerCase().includes(term)
  })

  const activePayments = payments.filter(p => !p.voided_at)
  const totalReceived = activePayments.reduce((sum, p) => sum + Number(p.amount || 0), 0)
  const thisMonthPayments = activePayments.filter(p => {
    const d = new Date(p.payment_date)
    const now = new Date()
    return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear()
  })
  const thisMonthTotal = thisMonthPayments.reduce((sum, p) => sum + Number(p.amount || 0), 0)

  const PAYMENTS_COLUMNS = [
    { id: 'date', label: 'Date' },
    { id: 'customer', label: 'Customer' },
    { id: 'invoice', label: 'Invoice' },
    { id: 'method', label: 'Method' },
    { id: 'reference', label: 'Reference' },
    { id: 'amount', label: 'Amount' },
    { id: 'actions', label: 'Actions' },
  ]
  const { visibleColumns, toggleColumn } = useColumnSelector(PAYMENTS_COLUMNS, { storageKey: 'payments' })

  const fmtMoney = (n: number) =>
    `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
  const outstandingDue = invoiceOptions.reduce((s, i) => s + (i.balance_due || 0), 0)

  return (
    <MobilePage>
      <MobilePageHeader
        title="Payments"
        subtitle={<span className="hidden sm:inline">Record and track customer payments</span>}
        actions={
          !showPaymentForm ? (
            <button type="button" onClick={() => setShowPaymentForm(true)} className={mobile.successBtn}>
              <PlusIcon className="w-4 h-4" />
              <span className="sm:hidden">Record</span>
              <span className="hidden sm:inline">Record Payment</span>
            </button>
          ) : undefined
        }
      />

      <div className="lg:hidden">
        <MobileStatStrip
          stats={[
            { key: 'total', label: 'Received', value: fmtMoney(totalReceived), tone: 'green' },
            { key: 'month', label: 'This month', value: fmtMoney(thisMonthTotal), tone: 'blue' },
            { key: 'open', label: 'Open invoices', value: String(invoiceOptions.length), tone: 'amber' },
          ]}
        />
      </div>

      <div className="hidden lg:grid grid-cols-3 gap-6">
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Received</p>
              <p className="mt-2 text-2xl font-bold text-green-700">{fmtMoney(totalReceived)}</p>
              <p className="text-xs text-gray-500 mt-1">{activePayments.length} payment{activePayments.length !== 1 ? 's' : ''}</p>
            </div>
            <div className="p-3 rounded-lg bg-green-50 text-green-600">
              <DollarSignIcon className="w-6 h-6" />
            </div>
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">This Month</p>
              <p className="mt-2 text-2xl font-bold text-blue-700">{fmtMoney(thisMonthTotal)}</p>
              <p className="text-xs text-gray-500 mt-1">{thisMonthPayments.length} payment{thisMonthPayments.length !== 1 ? 's' : ''}</p>
            </div>
            <div className="p-3 rounded-lg bg-blue-50 text-blue-600">
              <CalendarIcon className="w-6 h-6" />
            </div>
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Outstanding Invoices</p>
              <p className="mt-2 text-2xl font-bold text-yellow-700">{invoiceOptions.length}</p>
              <p className="text-xs text-gray-500 mt-1">{fmtMoney(outstandingDue)} due</p>
            </div>
            <div className="p-3 rounded-lg bg-yellow-50 text-yellow-600">
              <FileTextIcon className="w-6 h-6" />
            </div>
          </div>
        </div>
      </div>

      {showPaymentForm && (
        <MobileSection
          title="Record Payment"
          description="Apply a payment to an open invoice"
          actions={
            <button
              type="button"
              onClick={() => { setShowPaymentForm(false); setSelectedInvoice(null) }}
              className={mobile.ghostBtn}
              aria-label="Close"
            >
              <XIcon className="w-5 h-5" />
            </button>
          }
        >
          <form onSubmit={handleSubmitPayment} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="sm:col-span-2">
                <label className={mobile.label}>
                  Invoice <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.invoice_id}
                  onChange={(e) => handleInvoiceSelect(e.target.value)}
                  required
                  className={mobile.field}
                >
                  <option value="">Select an invoice...</option>
                  {invoiceOptions.map(inv => (
                    <option key={inv.id} value={inv.id}>
                      {inv.invoice_number} — {inv.customer?.company_name || 'Unknown'} — ${inv.balance_due.toLocaleString(undefined, { minimumFractionDigits: 2 })} due
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className={mobile.label}>
                  Payment Date <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  value={formData.payment_date}
                  onChange={(e) => setFormData(prev => ({ ...prev, payment_date: e.target.value }))}
                  required
                  className={mobile.field}
                />
              </div>

              <div>
                <label className={mobile.label}>
                  Amount <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                  <input
                    type="number"
                    step="0.01"
                    min="0.01"
                    value={formData.amount}
                    onChange={(e) => setFormData(prev => ({ ...prev, amount: e.target.value }))}
                    required
                    placeholder="0.00"
                    className={`${mobile.field} pl-7`}
                  />
                </div>
                {selectedInvoice && (
                  <p className="text-xs text-gray-500 mt-1">
                    Total ${selectedInvoice.total.toLocaleString(undefined, { minimumFractionDigits: 2 })} · Due $
                    {selectedInvoice.balance_due.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                  </p>
                )}
              </div>

              <div>
                <label className={mobile.label}>
                  Payment Method <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.payment_method}
                  onChange={(e) => setFormData(prev => ({ ...prev, payment_method: e.target.value }))}
                  required
                  className={mobile.field}
                >
                  {PAYMENT_METHODS.map(m => (
                    <option key={m.value} value={m.value}>{m.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className={mobile.label}>Reference / Check #</label>
                <input
                  type="text"
                  value={formData.reference_number}
                  onChange={(e) => setFormData(prev => ({ ...prev, reference_number: e.target.value }))}
                  placeholder="Check number, transaction ID, etc."
                  className={mobile.field}
                />
              </div>

              <div className="sm:col-span-2">
                <label className={mobile.label}>Notes</label>
                <input
                  type="text"
                  value={formData.notes}
                  onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                  placeholder="Optional payment notes"
                  className={mobile.field}
                />
              </div>
            </div>

            <div className="flex flex-col gap-2 p-3 bg-gray-50 rounded-xl border border-gray-200">
              <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer min-h-[44px]">
                <input
                  type="checkbox"
                  checked={formData.sync_invoice_to_qb}
                  onChange={(e) => setFormData(prev => ({ ...prev, sync_invoice_to_qb: e.target.checked }))}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 w-5 h-5"
                />
                QB: sync this invoice to QuickBooks
              </label>
              <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer min-h-[44px]">
                <input
                  type="checkbox"
                  checked={formData.sync_payment_to_qb}
                  onChange={(e) => setFormData(prev => ({ ...prev, sync_payment_to_qb: e.target.checked }))}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 w-5 h-5"
                />
                QB: sync this payment to QuickBooks
              </label>
            </div>

            {selectedInvoice && (
              <div className="bg-blue-50 rounded-xl p-4 border border-blue-200">
                <h3 className="text-sm font-semibold text-blue-900 mb-2 flex items-center gap-2">
                  <CreditCardIcon className="w-4 h-4" />
                  Invoice Summary
                </h3>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <p className="text-blue-700">Invoice</p>
                    <p className="font-semibold text-blue-900">{selectedInvoice.invoice_number}</p>
                  </div>
                  <div>
                    <p className="text-blue-700">Total</p>
                    <p className="font-semibold text-blue-900 tabular-nums">
                      ${selectedInvoice.total.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                    </p>
                  </div>
                  <div>
                    <p className="text-blue-700">Already Paid</p>
                    <p className="font-semibold text-blue-900 tabular-nums">
                      ${(selectedInvoice.amount_paid || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                    </p>
                  </div>
                  <div>
                    <p className="text-blue-700">Balance Due</p>
                    <p className="font-semibold text-blue-900 tabular-nums">
                      ${selectedInvoice.balance_due.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                    </p>
                  </div>
                </div>
                {parseFloat(formData.amount) > 0 && (
                  <div className="mt-3 pt-3 border-t border-blue-200">
                    <div className="flex justify-between text-sm gap-2">
                      <span className="text-blue-700">After this payment</span>
                      <span className="font-bold text-blue-900 tabular-nums">
                        ${Math.max(0, selectedInvoice.balance_due - parseFloat(formData.amount || '0')).toLocaleString(undefined, { minimumFractionDigits: 2 })} remaining
                      </span>
                    </div>
                    {parseFloat(formData.amount || '0') >= selectedInvoice.balance_due && (
                      <p className="text-xs text-green-700 mt-1 flex items-center gap-1">
                        <CheckCircleIcon className="w-3 h-3" /> This payment will mark the invoice as fully paid
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}

            <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
              <button
                type="button"
                onClick={() => { setShowPaymentForm(false); setSelectedInvoice(null) }}
                className={`${mobile.secondaryBtn} w-full sm:w-auto`}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving}
                className={`${mobile.successBtn} w-full sm:w-auto`}
              >
                {saving ? (
                  <>
                    <Loader2Icon className="w-4 h-4 animate-spin" />
                    Recording...
                  </>
                ) : (
                  <>
                    <CheckCircleIcon className="w-4 h-4" />
                    Record Payment
                  </>
                )}
              </button>
            </div>
          </form>
        </MobileSection>
      )}

      <MobileFilterBar
        search={searchTerm}
        onSearchChange={setSearchTerm}
        searchPlaceholder="Search payments..."
        className="lg:p-6"
      >
        <div className="hidden lg:block">
          <ColumnSelector
            columns={PAYMENTS_COLUMNS}
            visibleColumns={visibleColumns}
            onToggle={toggleColumn}
          />
        </div>
      </MobileFilterBar>

      <div className="native-mobile-list space-y-2">
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2Icon className="w-8 h-8 animate-spin text-blue-600" />
          </div>
        ) : filteredPayments.length === 0 ? (
          <MobileEmptyState
            icon={<DollarSignIcon className="w-12 h-12" />}
            title={searchTerm ? 'No payments match your search' : 'No payments recorded yet'}
            action={
              !searchTerm && !showPaymentForm ? (
                <button type="button" onClick={() => setShowPaymentForm(true)} className={mobile.successBtn}>
                  Record First Payment
                </button>
              ) : undefined
            }
          />
        ) : (
          filteredPayments.map((payment) => (
            <MobileListRow
              key={payment.id}
              onClick={() => navigate(`/invoice-generation?invoice=${payment.invoice_id}`)}
              title={getCustomerName(payment)}
              subtitle={payment.invoice?.invoice_number || 'N/A'}
              badges={
                <>
                  <span className={`${mobile.badge} bg-gray-100 text-gray-800`}>
                    {getMethodLabel(payment.payment_method)}
                  </span>
                  {payment.voided_at && (
                    <span className={`${mobile.badge} bg-red-100 text-red-800`}>Voided</span>
                  )}
                </>
              }
              meta={
                <>
                  <div>{new Date(payment.payment_date).toLocaleDateString()}</div>
                  {payment.reference_number && <div>Ref {payment.reference_number}</div>}
                </>
              }
              trailing={
                <span className={`text-sm font-semibold tabular-nums flex-shrink-0 ${payment.voided_at ? 'text-gray-400' : 'text-green-700'}`}>
                  {fmtMoney(payment.amount)}
                </span>
              }
              chevron={false}
              footer={
                !payment.voided_at ? (
                  <MobileRowAction tone="danger" onClick={() => handleVoidPayment(payment)}>
                    Void
                  </MobileRowAction>
                ) : undefined
              }
            />
          ))
        )}
      </div>

      <div className="native-desktop-table bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">Payment History</h2>
        </div>
        {loading ? (
          <div className="px-6 py-12 text-center">
            <Loader2Icon className="w-8 h-8 animate-spin text-blue-600 mx-auto mb-2" />
            <div className="text-gray-500">Loading payments...</div>
          </div>
        ) : filteredPayments.length === 0 ? (
          <div className="px-6 py-12 text-center">
            <DollarSignIcon className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <div className="text-gray-500 mb-2">
              {searchTerm ? 'No payments match your search' : 'No payments recorded yet'}
            </div>
            {!searchTerm && !showPaymentForm && (
              <button
                onClick={() => setShowPaymentForm(true)}
                className="mt-4 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm"
              >
                Record First Payment
              </button>
            )}
          </div>
        ) : (
          <div className="w-full">
            <table className="w-full table-fixed">
              <thead className="bg-gray-50">
                <tr>
                  {visibleColumns.has('date') && (
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                  )}
                  {visibleColumns.has('customer') && (
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Customer</th>
                  )}
                  {visibleColumns.has('invoice') && (
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Invoice</th>
                  )}
                  {visibleColumns.has('method') && (
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Method</th>
                  )}
                  {visibleColumns.has('reference') && (
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Reference</th>
                  )}
                  {visibleColumns.has('amount') && (
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                  )}
                  {visibleColumns.has('actions') && (
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  )}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredPayments.map((payment) => (
                  <tr key={payment.id} className={`hover:bg-gray-50 ${payment.voided_at ? 'opacity-50' : ''}`}>
                    {visibleColumns.has('date') && (
                      <td className="px-3 py-3 min-w-0 text-sm text-gray-900 truncate">
                        {new Date(payment.payment_date).toLocaleDateString()}
                      </td>
                    )}
                    {visibleColumns.has('customer') && (
                      <td className="px-3 py-3 min-w-0 text-sm text-gray-900 truncate">
                        {getCustomerName(payment)}
                      </td>
                    )}
                    {visibleColumns.has('invoice') && (
                      <td className="px-3 py-3 min-w-0 text-sm">
                        <button
                          onClick={() => navigate(`/invoice-generation?invoice=${payment.invoice_id}`)}
                          className="text-blue-600 hover:text-blue-900 hover:underline"
                        >
                          {payment.invoice?.invoice_number || 'N/A'}
                        </button>
                      </td>
                    )}
                    {visibleColumns.has('method') && (
                      <td className="px-3 py-3 min-w-0">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                          {getMethodLabel(payment.payment_method)}
                        </span>
                      </td>
                    )}
                    {visibleColumns.has('reference') && (
                      <td className="px-3 py-3 min-w-0 text-sm text-gray-500 truncate">
                        {payment.reference_number || '—'}
                      </td>
                    )}
                    {visibleColumns.has('amount') && (
                      <td className="px-3 py-3 min-w-0 text-sm font-semibold text-green-700 text-right">
                        {fmtMoney(payment.amount)}
                        {payment.voided_at && (
                          <span className="block text-xs font-normal text-red-600">Voided</span>
                        )}
                      </td>
                    )}
                    {visibleColumns.has('actions') && (
                      <td className="px-3 py-3 min-w-0 text-sm">
                        {!payment.voided_at && (
                          <button
                            onClick={() => handleVoidPayment(payment)}
                            className="text-red-600 hover:text-red-900 text-xs font-medium"
                            title="Void payment"
                          >
                            Void
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </MobilePage>
  )
}
